/* This example requires Tailwind CSS v2.0+ */
import React, {Fragment, useEffect} from 'react';
import {Transition} from '@headlessui/react';
import {ExclamationIcon as ExclamationOutline} from '@heroicons/react/outline';
import {CheckCircleIcon, ExclamationIcon, XCircleIcon, XIcon} from '@heroicons/react/solid';
import cs from 'classnames';
import {useAccountDetailsQuery, useCreateStripeDeepLinkMutation} from "@graphql/generated-graphql";
import {getClient} from "@graphql/graphql-client/graphClient";
import {useUniversalModalDispatch} from "@components/context/UniversalModalContext";
import {SimpleModal} from "@components/base/overlays/Modal/SimpleModal";
import NextImage from "next/image";
import SheriffOctopus from "@public/svgs/sheriff-octopus.svg";
import SmileyLifeSaver from "@public/svgs/smiley-life-saver.svg";
import {useRouter} from "next/router";

export type NotifcationProps = {
    title: string;
    text: React.ReactNode;
    type: 'warning' | 'danger' | 'success';
    hideClose?: boolean;
};

type UncontrolledProps = {
    show: boolean;
    setShow: (val: boolean) => void;
};

export function Notification({
                                 show,
                                 setShow,
                                 title,
                                 text,
                                 type,
                                 hideClose,
                             }: NotifcationProps & UncontrolledProps) {
    let Icon = XCircleIcon;
    let iconClass = '';
    if (type === 'success') {
        Icon = CheckCircleIcon;
        iconClass = 'text-green-400';
    } else if (type === 'warning') {
        Icon = ExclamationIcon;
        iconClass = 'text-sunshine-600';
    } else {
        Icon = ExclamationOutline;
        iconClass = 'text-red-600';
    }

    const containerPositioning = cs('w-full h-full flex flex-col items-center space-y-4', {
        'sm:items-center sm:justify-end': type === 'success',
        'sm:items-end sm:justify-start': type !== 'success',
    });

    const router = useRouter();
    const createStripeDeepLink = useCreateStripeDeepLinkMutation(getClient('users'), {
        onSuccess: (data) => {
            if (data?.createStripeDeepLink?.url) {
                router.push(data.createStripeDeepLink.url);
            }
        }
    });
    const {data: accountData} = useAccountDetailsQuery(getClient('users'), undefined, {
        staleTime: 60 * 1000 * 5,
    });
    const {toggle} = useUniversalModalDispatch();
    useEffect(() => {
        const paymentWarningSuppressedAt = window.localStorage.getItem('paymentWarningSuppressedAt');
        if (paymentWarningSuppressedAt) {
            const suppressedDate = new Date(paymentWarningSuppressedAt);
            if (new Date().getTime() - suppressedDate.getTime() < 1000 * 3600 * 24)
                return;
        }
        if (accountData?.account?.subscriptionPastDue || accountData?.account?.subscriptionCancelled) {
            window.localStorage.setItem('paymentWarningSuppressedAt', new Date().toISOString());
            toggle(
                true,
                <SimpleModal
                    body={<>
                        <div className={"mt-8"}>
                            <NextImage
                                src={accountData?.account?.subscriptionPastDue ? SheriffOctopus : SmileyLifeSaver}
                                width={104}
                                layout="fixed"
                                className="pointer-events-none"
                            />
                        </div>
                        <div className={"text-xl font-medium my-4"}>
                            {accountData?.account?.subscriptionPastDue
                                ? 'Hey partner! Your payment failed'
                                : 'Hey Captain, your account\'s adrift!'}
                        </div>
                        <div className={"px-8"}>
                            <div className={""}>
                                {accountData?.account?.subscriptionPastDue
                                    ? 'Simply update your payment details to ensure uninterrupted access to services and courses you own.'
                                    : "Don't worry - we're here to throw you a lifeline! When you update your payment, your account will be reactivated so you can access your courses."}
                            </div>
                        </div>
                    </>}
                    confirmLabel={accountData?.account?.subscriptionPastDue ? 'Update payment' : 'Reactivate your account'}
                    onConfirm={() => createStripeDeepLink.mutate({type: 'manage-payment'})}
                />,
            );
        }
    }, [accountData]);

    return (
        <>
            {accountData?.account?.subscriptionPastDue && (
                <div className="bg-red-500 text-white text-sm text-center p-2">
                    We are unable to renew your Skipper subscription. Please
                    <a href="#" className="underline px-1" onClick={() => createStripeDeepLink.mutate({type: 'manage-payment'})}>
                        update your payment information
                    </a>
                    immediately to avoid losing access to your courses.
                </div>
            )}
            {accountData?.account?.subscriptionCancelled && (
                <div className="bg-red-500 text-white text-sm text-center p-2">
                    We are unable to renew your Skipper subscription. Please
                    <a href="#" className="underline px-1" onClick={() => createStripeDeepLink.mutate({type: 'manage-payment'})}>
                        update your payment information
                    </a>
                    to regain access to your courses.
                </div>
            )}
            <div
                tabIndex={0}
                aria-live="assertive"
                className="fixed inset-0 flex items-center px-4 py-6 pointer-events-none sm:p-6 sm:items-start z-9999"
            >
                <div className={containerPositioning}>
                    {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
                    <Transition
                        show={show}
                        as={Fragment}
                        enter="transform ease-out duration-300 transition"
                        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
                        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div
                            className="max-w-screen-md  bg-gray-900 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden"
                            aria-disabled
                            tabIndex={0}
                        >
                            <div className="p-4 text-white">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0">
                                        <Icon
                                            className={`h-8 w-8 ${iconClass}`}
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="ml-3 flex-1 pt-0.5 px-5">
                                        <p className="text-lg font-medium whitespace-nowrap">
                                            {title}
                                        </p>
                                        {typeof text === 'string' ? (
                                            <p className="mt-1 text-base">{text}</p>
                                        ) : (
                                            text
                                        )}
                                    </div>
                                    <div className="ml-10 flex-shrink-0 flex">
                                        {!hideClose && (
                                            <button
                                                className="bg-transparent rounded-md inline-flex hover:text-teal-3 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                                                onClick={(e) => {
                                                    setShow(false);
                                                }}
                                            >
                                                <span className="sr-only">Close</span>
                                                <XIcon className="h-5 w-5" aria-hidden="true"/>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </>
    );
}
